/**
 * Popup Component
 */
class PopupDragAdapter {
    constructor(root, options = {}) {
        this.root = root;
        this.handle = root?.querySelector(options.handleSelector || '');
        this.storageKey = options.storageKey || '';
        this.minWidth = options.minWidth || 545;
        this.margin = options.margin || 8;
        this.drag = null;

        if (!this.root || !this.handle || typeof PointerEvent === 'undefined') return;

        this.init();
    }

    init() {
        this.handle.addEventListener('pointerdown', this.onPointerDown);
        window.addEventListener('resize', this.onResize, { passive: true });
        this.applyStoredPosition();
    }

    onPointerDown = (event) => {
        if (!this.canDrag() || (event.button !== undefined && event.button !== 0)) {
            return;
        }

        const rect = this.root.getBoundingClientRect();
        this.drag = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            left: rect.left,
            top: rect.top,
        };

        this.root.classList.add('is-dragging');
        this.handle.setPointerCapture?.(event.pointerId);
        this.applyPosition(rect.left, rect.top);

        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.onPointerUp);
        event.preventDefault();
    };

    onPointerMove = (event) => {
        if (!this.drag || event.pointerId !== this.drag.pointerId) {
            return;
        }

        const nextLeft = this.drag.left + event.clientX - this.drag.startX;
        const nextTop = this.drag.top + event.clientY - this.drag.startY;
        this.applyPosition(nextLeft, nextTop);
    };

    onPointerUp = (event) => {
        if (!this.drag || event.pointerId !== this.drag.pointerId) {
            return;
        }

        this.handle.releasePointerCapture?.(event.pointerId);
        this.root.classList.remove('is-dragging');
        this.drag = null;

        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
        this.savePosition();
    };

    onResize = () => {
        if (!this.canDrag()) {
            this.resetInlinePosition();
            return;
        }

        this.applyStoredPosition();
    };

    canDrag() {
        return window.innerWidth >= this.minWidth;
    }

    applyStoredPosition() {
        if (!this.canDrag()) {
            this.resetInlinePosition();
            return;
        }

        const position = this.readPosition();
        if (!position) return;

        this.applyPosition(position.left, position.top);
    }

    applyPosition(left, top) {
        const rect = this.root.getBoundingClientRect();
        const maxLeft = Math.max(this.margin, window.innerWidth - rect.width - this.margin);
        const maxTop = Math.max(this.margin, window.innerHeight - rect.height - this.margin);
        const nextLeft = Math.min(Math.max(left, this.margin), maxLeft);
        const nextTop = Math.min(Math.max(top, this.margin), maxTop);

        this.root.classList.add('is-user-positioned');
        this.root.style.left = `${nextLeft}px`;
        this.root.style.top = `${nextTop}px`;
        this.root.style.right = 'auto';
        this.root.style.bottom = 'auto';
        this.root.style.transform = 'none';
    }

    resetInlinePosition() {
        this.root.classList.remove('is-user-positioned', 'is-dragging');
        ['left', 'top', 'right', 'bottom', 'transform'].forEach((prop) => {
            this.root.style.removeProperty(prop);
        });
    }

    readPosition() {
        if (!this.storageKey) return null;

        try {
            const position = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
            if (!position || !Number.isFinite(position.left) || !Number.isFinite(position.top)) {
                return null;
            }
            return position;
        } catch (error) {
            return null;
        }
    }

    savePosition() {
        if (!this.storageKey) return;

        const rect = this.root.getBoundingClientRect();
        try {
            localStorage.setItem(this.storageKey, JSON.stringify({
                left: Math.round(rect.left),
                top: Math.round(rect.top),
            }));
        } catch (error) {
            // localStorage may be unavailable in private or restricted contexts.
        }
    }
}

export class Popup {
    constructor() {
        this.wrap = document.querySelector('.main-popup-wrap, .no-popup');
        
        if (!this.wrap) return;
        
        this.init();
    }

    init() {
        const closeButtons = this.wrap.querySelectorAll('.popup-close, .no-popup-ctrl__close');
        const swiperRoot = this.wrap.querySelector('.main-popup-slide, .popup-swiper');
        const popupInner = this.wrap.querySelector('.main-popup, .no-popup__inner');
        const popupCount = Number(popupInner?.dataset.count || 0);

        // Swiper 초기화
        if (typeof Swiper !== 'undefined' && swiperRoot) {
            new Swiper(swiperRoot, {
                slidesPerView: 1,
                loop: popupCount > 1,
                watchOverflow: true,
                autoHeight: true,
                effect: 'fade',
                fadeEffect: {
                    crossFade: true,
                },
                navigation: {
                    nextEl: '[data-index="next"]',
                    prevEl: '[data-index="prev"]',
                },
                pagination: {
                    el: '.popup-pager',
                    clickable: true,
                },
                speed: 500,
                spaceBetween: 0,
            });
        }

        new PopupDragAdapter(this.wrap, {
            handleSelector: '.no-popup__drag-handle',
            storageKey: this.wrap.dataset.popupStorageKey,
            minWidth: 545,
            margin: 8,
        });

        // 팝업 닫기
        const closePopup = () => {
            const checked = this.wrap.querySelector('.no-popup__today-check:checked');
            if (checked) {
                const expires = new Date();
                expires.setHours(23, 59, 59, 999);
                document.cookie = `popupClosed=true; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
            }
            this.wrap.style.display = 'none';
        };

        closeButtons.forEach((button) => {
            button.addEventListener('click', closePopup);
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                closePopup();
            }
        });
    }
}

